import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HoverIdComponent } from '../hover-id/hover-id.component';

export interface DevicesTableItem {
  id: string;
  clinicId: string;
  clinicName?: string | null;
  deviceTypeId: string;
  deviceTypeName?: string | null;
  serialNumber: string | null;
  brand: string | null;
  model: string | null;
  status: string;
  createdAt: string;
  photos?: unknown[];
}

@Component({
  selector: 'lib-devices-table',
  standalone: true,
  imports: [CommonModule, DatePipe, HoverIdComponent],
  template: `
    <div class="devices-table__wrap">
      <table class="devices-table">
        <thead>
          <tr>
            <th>Clinic</th>
            <th>Device Type</th>
            <th>Brand / Model</th>
            <th>Serial</th>
            @if (showPhotosColumn) {
              <th>Photos</th>
            }
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (item of items; track item.id) {
            <tr class="devices-table__row" (click)="rowSelected.emit(item)">
              <td>
                <lib-hover-id [value]="hoverId(item.clinicId)">
                  @if (canOpenClinic && item.clinicId.trim()) {
                    <button
                      type="button"
                      class="devices-table__link-button"
                      (click)="onClinicClick(item, $event)"
                      [title]="item.clinicName || clinicName(item.clinicId)"
                    >
                      {{ item.clinicName || clinicName(item.clinicId) }}
                    </button>
                  } @else {
                    <span [title]="item.clinicName || clinicName(item.clinicId)">
                      {{ item.clinicName || clinicName(item.clinicId) }}
                    </span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="hoverId(item.deviceTypeId)">
                  @if (canOpenDeviceType && item.deviceTypeId.trim()) {
                    <button
                      type="button"
                      class="devices-table__link-button"
                      (click)="onDeviceTypeClick(item, $event)"
                      [title]="item.deviceTypeName || deviceTypeName(item.deviceTypeId)"
                    >
                      {{ item.deviceTypeName || deviceTypeName(item.deviceTypeId) }}
                    </button>
                  } @else {
                    <span [title]="item.deviceTypeName || deviceTypeName(item.deviceTypeId)">
                      {{ item.deviceTypeName || deviceTypeName(item.deviceTypeId) }}
                    </span>
                  }
                </lib-hover-id>
              </td>
              <td>
                <lib-hover-id [value]="hoverId(item.id)">
                  <span [title]="brandModelLabel(item)">{{ brandModelLabel(item) }}</span>
                </lib-hover-id>
              </td>
              <td>{{ item.serialNumber ?? '—' }}</td>
              @if (showPhotosColumn) {
                <td>{{ item.photos?.length ?? 0 }}</td>
              }
              <td>
                <span class="devices-table__status" [ngClass]="statusClass(item.status)">
                  {{ statusLabel(item.status) }}
                </span>
              </td>
              <td>{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td>
                <button type="button" class="devices-table__action" (click)="onActionClick(item, $event)">
                  {{ actionLabel }}
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="devices-table__cards">
        @for (item of items; track item.id) {
          <article class="devices-table__card" (click)="rowSelected.emit(item)">
            <div class="devices-table__card-head">
              <div class="devices-table__card-title">
                <lib-hover-id [value]="hoverId(item.id)">
                  <span [title]="brandModelLabel(item)">{{ brandModelLabel(item) }}</span>
                </lib-hover-id>
              </div>
              <div class="devices-table__card-status" [ngClass]="statusClass(item.status)">
                {{ statusLabel(item.status) }}
              </div>
            </div>

            <div class="devices-table__card-grid">
              <div class="devices-table__card-row">
                <div class="devices-table__card-label">Clinic</div>
                <div class="devices-table__card-value">
                  <lib-hover-id [value]="hoverId(item.clinicId)">
                    @if (canOpenClinic && item.clinicId.trim()) {
                      <button
                        type="button"
                        class="devices-table__link-button"
                        (click)="onClinicClick(item, $event)"
                        [title]="item.clinicName || clinicName(item.clinicId)"
                      >
                        {{ item.clinicName || clinicName(item.clinicId) }}
                      </button>
                    } @else {
                      <span [title]="item.clinicName || clinicName(item.clinicId)">
                        {{ item.clinicName || clinicName(item.clinicId) }}
                      </span>
                    }
                  </lib-hover-id>
                </div>
              </div>

              <div class="devices-table__card-row">
                <div class="devices-table__card-label">Device Type</div>
                <div class="devices-table__card-value">
                  <lib-hover-id [value]="hoverId(item.deviceTypeId)">
                    @if (canOpenDeviceType && item.deviceTypeId.trim()) {
                      <button
                        type="button"
                        class="devices-table__link-button"
                        (click)="onDeviceTypeClick(item, $event)"
                        [title]="item.deviceTypeName || deviceTypeName(item.deviceTypeId)"
                      >
                        {{ item.deviceTypeName || deviceTypeName(item.deviceTypeId) }}
                      </button>
                    } @else {
                      <span [title]="item.deviceTypeName || deviceTypeName(item.deviceTypeId)">
                        {{ item.deviceTypeName || deviceTypeName(item.deviceTypeId) }}
                      </span>
                    }
                  </lib-hover-id>
                </div>
              </div>

              <div class="devices-table__card-row">
                <div class="devices-table__card-label">Serial</div>
                <div class="devices-table__card-value">{{ item.serialNumber ?? '—' }}</div>
              </div>

              @if (showPhotosColumn) {
                <div class="devices-table__card-row">
                  <div class="devices-table__card-label">Photos</div>
                  <div class="devices-table__card-value">{{ item.photos?.length ?? 0 }}</div>
                </div>
              }

              <div class="devices-table__card-row">
                <div class="devices-table__card-label">Created</div>
                <div class="devices-table__card-value">{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</div>
              </div>
            </div>

            <div class="devices-table__card-actions" (click)="$event.stopPropagation()">
              <button type="button" class="devices-table__action" (click)="onActionClick(item, $event)">
                {{ actionLabel }}
              </button>
            </div>
          </article>
        }
      </div>
    </div>
  `,
  styles: [`
    .devices-table__wrap {
      overflow-x: auto;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      background: #ffffff;
    }

    .devices-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 900px;
    }

    .devices-table__cards {
      display: none;
    }

    .devices-table th,
    .devices-table td {
      border-bottom: 1px solid #f3f4f6;
      padding: 10px;
      text-align: left;
      vertical-align: middle;
      font-size: 13px;
    }

    .devices-table__row {
      cursor: pointer;
    }

    .devices-table__row:hover {
      background: #f8fafc;
    }

    .devices-table__link-button {
      appearance: none;
      -webkit-appearance: none;
      border: 0;
      background: transparent;
      color: #2563eb;
      padding: 0;
      font: inherit;
      font-weight: 600;
      text-align: left;
      text-decoration: underline;
      text-underline-offset: 2px;
      cursor: pointer;
    }

    .devices-table__link-button:hover {
      color: #1d4ed8;
    }

    .devices-table__status,
    .devices-table__card-status {
      display: inline-flex;
      align-items: center;
      min-height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      text-transform: capitalize;
    }

    .devices-table__status--active {
      border-color: #bbf7d0;
      background: #dcfce7;
      color: #166534;
    }

    .devices-table__status--maintenance {
      border-color: #fde68a;
      background: #fef3c7;
      color: #92400e;
    }

    .devices-table__status--broken {
      border-color: #fecaca;
      background: #fee2e2;
      color: #991b1b;
    }

    .devices-table__status--retired {
      border-color: #e2e8f0;
      background: #f1f5f9;
      color: #475569;
    }

    .devices-table__action {
      height: 38px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #fff;
      padding: 0 12px;
      cursor: pointer;
    }

    .devices-table__card {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      cursor: pointer;
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
    }

    .devices-table__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .devices-table__card-title {
      color: #111827;
      font-size: 15px;
      font-weight: 700;
      overflow-wrap: anywhere;
    }

    .devices-table__card-grid {
      display: grid;
      gap: 10px;
    }

    .devices-table__card-row {
      display: grid;
      grid-template-columns: minmax(0, 96px) minmax(0, 1fr);
      gap: 10px;
      align-items: start;
    }

    .devices-table__card-label {
      color: #64748b;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .devices-table__card-value {
      color: #111827;
      font-size: 13px;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }

    .devices-table__card-actions {
      display: flex;
      justify-content: flex-end;
      padding-top: 6px;
      border-top: 1px solid #f1f5f9;
    }

    @media (max-width: 900px) {
      .devices-table__wrap {
        padding: 10px;
        display: grid;
        gap: 12px;
        background: #f8fafc;
      }

      .devices-table {
        display: none;
      }

      .devices-table__cards {
        display: grid;
        gap: 12px;
      }
    }

    @media (max-width: 640px) {

      .devices-table__card-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }
    }
  `],
})
export class DevicesTableComponent {
  @Input() public items: DevicesTableItem[] = [];
  @Input() public actionLabel = 'Lifecycle';
  @Input() public showPhotosColumn = true;
  @Input() public canOpenClinic = false;
  @Input() public canOpenDeviceType = false;
  @Input() public clinicNameResolver: ((id: string) => string) | null = null;
  @Input() public deviceTypeNameResolver: ((id: string) => string) | null = null;

  @Output() public readonly rowSelected = new EventEmitter<DevicesTableItem>();
  @Output() public readonly actionClicked = new EventEmitter<DevicesTableItem>();
  @Output() public readonly clinicOpenClicked = new EventEmitter<string>();
  @Output() public readonly deviceTypeOpenClicked = new EventEmitter<string>();

  protected onActionClick(item: DevicesTableItem, event: MouseEvent): void {
    event.stopPropagation();
    this.actionClicked.emit(item);
  }

  protected onClinicClick(item: DevicesTableItem, event: MouseEvent): void {
    event.stopPropagation();
    if (item.clinicId.trim()) {
      this.clinicOpenClicked.emit(item.clinicId);
    }
  }

  protected onDeviceTypeClick(item: DevicesTableItem, event: MouseEvent): void {
    event.stopPropagation();
    if (item.deviceTypeId.trim()) {
      this.deviceTypeOpenClicked.emit(item.deviceTypeId);
    }
  }

  protected clinicName(id: string): string {
    if (this.clinicNameResolver) {
      return this.clinicNameResolver(id);
    }
    return this.readableId(id);
  }

  protected deviceTypeName(id: string): string {
    if (this.deviceTypeNameResolver) {
      return this.deviceTypeNameResolver(id);
    }
    return this.readableId(id);
  }

  protected hoverId(value: string | null | undefined): string | null {
    return value?.trim() || null;
  }

  protected brandModelLabel(item: DevicesTableItem): string {
    return `${item.brand || '—'} / ${item.model || '—'}`;
  }

  protected statusLabel(status: string): string {
    return status.trim() || '—';
  }

  protected statusClass(status: string): string {
    const normalized = status.trim().toLowerCase();
    return ['active', 'maintenance', 'broken', 'retired'].includes(normalized)
      ? `devices-table__status--${normalized}`
      : '';
  }

  protected readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (this.isUuidLike(normalized)) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    if (normalized.length > 26) {
      return `${normalized.slice(0, 16)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}
